import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import { schemas } from '@ciam/form-kit';
import { useLoadingSpinnerStore, useModal } from '@ciam/ui';
import { GigyaAPI } from '@ciam/gigya-api';
import { useTranslation } from '@ciam/i18n';
import { useValidatorStore } from '@features/proxy';
import { useLoginInfoStore } from '@features/shared';

const ERROR_CODES = {
  SUCCESS: 0,
  INVALID_CURRENT_PASSWORD: 403042,
  ALREADY_USED_PASSWORD: 400006,
} as const;

export const useSetAccountPasswordForm = () => {
  const { t } = useTranslation(['proxy', 'schemas']);
  const { handleCancel: closeModal } = useModal();
  const { showLoading, hideLoading } = useLoadingSpinnerStore();

  const setValidateStep = useValidatorStore((state) => state.setValidateStep);
  const { userInfo, clearLoginInfo } = useLoginInfoStore();

  const methods = useForm({
    mode: 'onChange',
    defaultValues: {
      currentPassword: '',
      newPassword: '',
      newPasswordConfirm: '',
    },
    resolver: zodResolver(
      z
        .object({
          currentPassword: schemas.password,
          newPassword: schemas.password,
          newPasswordConfirm: schemas.password,
        })
        .refine((data) => data.newPassword === data.newPasswordConfirm, {
          message: t('errors.not-match-new-password'),
          path: ['newPasswordConfirm'],
        }),
    ),
  });
  const { handleSubmit, setError } = methods;

  const handleSetRootError = (message: string) => {
    return setError('root', {
      type: 'manual',
      message: message,
    });
  };

  const onSubmit = handleSubmit(async (formData) => {
    showLoading();
    const res = await GigyaAPI.accounts.setAccountInfo({
      UID: userInfo.UID,
      password: formData.currentPassword,
      newPassword: formData.newPassword,
    });

    switch (res.errorCode) {
      case ERROR_CODES.SUCCESS:
        setValidateStep('user-status-check');
        return closeModal();
      case ERROR_CODES.INVALID_CURRENT_PASSWORD:
        hideLoading();
        return handleSetRootError(t('errors.invalid-current-password'));
      case ERROR_CODES.ALREADY_USED_PASSWORD:
        hideLoading();
        return handleSetRootError(t('errors.already-used-password'));
      default:
        hideLoading();
        clearLoginInfo();
        return handleSetRootError(t('errors.default'));
    }
  });

  return { ...methods, onSubmit };
};
